import { useSyncExternalStore } from 'react';
import { hapticSuccess, hapticWarn } from './native.js';

/**
 * O retorno das ações (T7.29, etapa 4): a vibração do sistema quando algo importante se
 * conclui (confirmou, comi, paguei, enviou) e quando é recusado, e um som curto de "feito"
 * só para quem ligar nos Ajustes. Sem vibração inventada: só os padrões do aparelho.
 *
 * Quem decide é a fila de envios do app (`MutationCache` em App.tsx): recusa sempre avisa;
 * conclusão avisa quando o envio leva `meta: { feel: 'done' }`.
 */
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** 'done': conclusão importante (vibra e, se ligado, toca o som). 'quiet': nem a recusa vibra. */
      feel?: 'done' | 'quiet';
    };
  }
}

const SOUND_KEY = 'secretario.som.feito';
const EVENT = 'secretario:som-feito';

export function readDoneSound(): boolean {
  try {
    return window.localStorage.getItem(SOUND_KEY) === 'sim';
  } catch {
    return false;
  }
}

export function setDoneSound(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(SOUND_KEY, 'sim');
    else window.localStorage.removeItem(SOUND_KEY);
  } catch {
    // Sem armazenamento: vale até recarregar.
  }
  window.dispatchEvent(new Event(EVENT));
  if (on) playDone();
}

export function useDoneSound(): boolean {
  return useSyncExternalStore(
    (listener) => {
      window.addEventListener(EVENT, listener);
      return () => window.removeEventListener(EVENT, listener);
    },
    readDoneSound,
    () => false,
  );
}

let audio: AudioContext | null = null;

/** Duas notas curtas e baixas, subindo: "feito". */
function playDone(): void {
  try {
    audio ??= new AudioContext();
    const context = audio;
    const now = context.currentTime;
    for (const [at, freq] of [
      [0, 784],
      [0.09, 1046],
    ] as const) {
      const osc = context.createOscillator();
      const gain = context.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + at);
      gain.gain.linearRampToValueAtTime(0.07, now + at + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.22);
      osc.connect(gain).connect(context.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.24);
    }
  } catch {
    // Sem áudio (navegador bloqueou): fica só a vibração.
  }
}

/** Uma conclusão importante. */
export function feelDone(): void {
  hapticSuccess();
  if (readDoneSound()) playDone();
}

/** Recusa ou erro de um envio. */
export function feelRefuse(): void {
  hapticWarn();
}
